import { Badge } from "@/components/ui/badge";
import { RecommendationStatus } from "@/lib/schema";
import { Brain } from "lucide-react";

interface RecommendationBadgeProps {
  recommendation?: RecommendationStatus;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  className?: string;
}

const getRecommendationDisplay = (recommendation?: RecommendationStatus) => {
  switch (recommendation) {
    case RecommendationStatus.HIGHLY_RECOMMENDED:
      return { 
        color: 'bg-green-100 text-green-800 border-0', 
        text: 'Highly Recommended',
        icon: 'text-green-600'
      };
    case RecommendationStatus.RECOMMENDED:
      return { 
        color: 'bg-blue-100 text-blue-800 border-0', 
        text: 'Recommended',
        icon: 'text-blue-600'
      };
    case RecommendationStatus.CONSIDER:
      return { 
        color: 'bg-yellow-100 text-yellow-800 border-0', 
        text: 'Consider',
        icon: 'text-yellow-600'
      };
    case RecommendationStatus.NOT_RECOMMENDED:
      return { 
        color: 'bg-red-100 text-red-800 border-0', 
        text: 'Not Recommended',
        icon: 'text-red-600'
      };
    default:
      return { 
        color: 'bg-gray-100 text-gray-800 border-0', 
        text: 'Pending Review',
        icon: 'text-gray-600'
      };
  }
};

const getSizeClasses = (size: 'sm' | 'md' | 'lg') => {
  switch (size) {
    case 'sm':
      return {
        badge: 'text-xs px-2 py-1',
        icon: 'h-3 w-3'
      };
    case 'md':
      return {
        badge: 'text-sm px-3 py-1.5',
        icon: 'h-4 w-4'
      };
    case 'lg':
      return {
        badge: 'text-base px-4 py-2',
        icon: 'h-5 w-5'
      };
    default:
      return {
        badge: 'text-xs px-2 py-1',
        icon: 'h-3 w-3'
      };
  }
};

export function RecommendationBadge({
  recommendation,
  size = 'sm',
  showIcon = false,
  className = ""
}: RecommendationBadgeProps) {
  if (!recommendation) {
    return null;
  }

  const display = getRecommendationDisplay(recommendation);
  const sizeClasses = getSizeClasses(size);

  return (
    <Badge 
      className={`
        ${display.color} 
        ${sizeClasses.badge}
        font-medium space-grotesk inline-flex items-center gap-1
        ${className}
      `}
    >
      {showIcon && (
        <Brain className={`${sizeClasses.icon} ${display.icon}`} />
      )}
      {display.text}
    </Badge>
  );
}
